import { decrypt } from '../encryption/decrypt';
import { StreamMessage } from '../protocol/StreamMessage';
import { PushPipeline } from '../utils/PushPipeline';
import { validateStreamMessage } from '../utils/validateStreamMessage';
import { MsgChainUtil } from './MsgChainUtil';
import { OrderMessages } from './ordering/OrderMessages';
import { StreamrClientError } from '../StreamrClientError';
import { isCompliantEncryptionType, isCompliantSignatureType } from '../utils/encryptionCompliance';
import { EncryptionType } from '@streamr/trackerless-network';
export const createMessagePipeline = (opts) => {
    const logger = opts.loggerFactory.createLogger('messagePipeline');
    const onError = (error, streamMessage) => {
        if (streamMessage) {
            ignoreMessages.add(streamMessage.messageId);
        }
        if (error && 'messageId' in error && error.messageId) {
            ignoreMessages.add(error.messageId);
        }
        throw error;
    };
    const messageStream = new PushPipeline;
    const msgChainUtil = new MsgChainUtil(async (msg) => {
        await validateStreamMessage(msg, opts.streamRegistry, opts.signatureValidator, opts.config);
        if (msg.encryptionType !== EncryptionType.NONE && !isCompliantEncryptionType(msg.encryptionType, opts.config)) {
            throw new StreamrClientError(`A message in stream ${msg.getStreamId()} was rejected because the encryption type violates configured requirements (encryptionType: ${msg.encryptionType})!`, 'ENCRYPTION_POLICY_VIOLATION', msg);
        }
        if (!isCompliantSignatureType(msg.signatureType, opts.config)) {
            throw new StreamrClientError(`A message in stream ${msg.getStreamId()} was rejected because the signature type violates configured requirements (signatureType: ${msg.encryptionType})!`, 'SIGNATURE_POLICY_VIOLATION', msg);
        }
        let decrypted;
        if (StreamMessage.isAESEncrypted(msg)) {
            try {
                decrypted = await decrypt(msg, opts.groupKeyManager, opts.destroySignal);
            }
            catch (err) {
                // TODO log this in onError? if we want to log all errors?
                logger.debug('Failed to decrypt', { messageId: msg.messageId, err });
                // clear cached permissions if cannot decrypt, likely permissions need updating
                opts.streamRegistry.invalidatePermissionCaches(msg.getStreamId());
                throw err;
            }
        }
        else {
            decrypted = msg;
        }
        decrypted.getParsedContent(); // throws if content is not parsable (e.g. not valid JSON)
        return decrypted;
    }, messageStream.onError);
    // collect messages that fail validation/parsing, do not push out of pipeline
    // NOTE: we let failed messages be processed and only removed at end so they don't
    // end up acting as gaps that we repeatedly try to fill.
    const ignoreMessages = new WeakSet();
    messageStream.onError.listen(onError);
    if (opts.config.orderMessages) {
        // order messages and fill gaps
        const orderMessages = new OrderMessages(opts.streamPartId, opts.getStorageNodes, () => { }, // TODO send some error to messageStream (NET-987)
        opts.resends, opts.config);
        messageStream.pipe(async function* (src) {
            setImmediate(() => {
                orderMessages.addMessages(src);
            });
            yield* orderMessages;
        });
        messageStream.onBeforeFinally.listen(() => {
            orderMessages.destroy();
        });
    }
    messageStream
        .pipe(async function* (src) {
        setImmediate(async () => {
            let err = undefined;
            try {
                for await (const msg of src) {
                    msgChainUtil.addMessage(msg);
                }
            }
            catch (e) {
                err = e;
            }
            await msgChainUtil.flush();
            msgChainUtil.stop(err);
        });
        yield* msgChainUtil;
    })
        // ignore any failed messages
        .filter((streamMessage) => {
        return !ignoreMessages.has(streamMessage.messageId);
    });
    return messageStream;
};
//# sourceMappingURL=messagePipeline.js.map